/**
 * ProfilePage - User profile and coach settings
 * Industrial-athletic aesthetic with premium dark theme
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Camera,
  Mail,
  Save,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Users,
  Sparkles,
  Check,
  X,
  Shield,
  Heart,
  Flame,
  Target,
  Apple,
  Loader2,
  ArrowLeft,
  Ruler,
  Scale,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { profiles, auth } from '../services/supabaseService';
import toast from 'react-hot-toast';

// Specialization options
const SPECIALIZATIONS = [
  { id: 'strength', label: 'Güç', icon: Dumbbell },
  { id: 'cardio', label: 'Kardiyo', icon: Flame },
  { id: 'weight_loss', label: 'Kilo Verme', icon: Target },
  { id: 'flexibility', label: 'Esneklik', icon: Sparkles },
  { id: 'nutrition', label: 'Beslenme', icon: Apple },
];

// Coach types
const COACH_TYPES = [
  { value: 'fitness', label: 'Fitness Koçu' },
  { value: 'nutrition', label: 'Beslenme Koçu' },
  { value: 'life', label: 'Yaşam Koçu' },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // User state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Profile form state
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Coach section state
  const [isCoachSectionOpen, setIsCoachSectionOpen] = useState(false);
  const [hasCoachProfile, setHasCoachProfile] = useState(false);
  const [wantsToBeCoach, setWantsToBeCoach] = useState(false);

  // Coach form state
  const [coachDisplayName, setCoachDisplayName] = useState('');
  const [coachBio, setCoachBio] = useState('');
  const [coachSpecializations, setCoachSpecializations] = useState([]);
  const [coachType, setCoachType] = useState('fitness');
  const [isAcceptingClients, setIsAcceptingClients] = useState(true);
  const [isSavingCoach, setIsSavingCoach] = useState(false);

  // Load user data
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      const currentUser = await auth.getUser();
      if (!currentUser) {
        navigate('/auth');
        return;
      }
      setUser(currentUser);

      // Load profile
      const profile = await profiles.get(currentUser.id);
      if (profile) {
        setDisplayName(profile.display_name || '');
        setAvatarUrl(profile.avatar_url || '');
        setHeightCm(profile.height_cm || '');
        setWeightKg(profile.weight_kg || '');
      }

      // Load coach profile
      const coachProfile = await profiles.getCoachProfile(currentUser.id);
      if (coachProfile) {
        setHasCoachProfile(true);
        setWantsToBeCoach(true);
        setIsCoachSectionOpen(true);
        setCoachDisplayName(coachProfile.display_name || '');
        setCoachBio(coachProfile.bio || '');
        setCoachSpecializations(coachProfile.specializations || []);
        setCoachType(coachProfile.coach_type || 'fitness');
        setIsAcceptingClients(coachProfile.is_accepting_clients ?? true);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Profil yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle avatar file selection
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalı');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Save profile
  const handleSaveProfile = async () => {
    if (!user) return;

    try {
      setIsSavingProfile(true);

      // Upload avatar if changed
      if (avatarFile) {
        await profiles.uploadAvatar(user.id, avatarFile);
        setAvatarFile(null);
        setAvatarPreview('');
      }

      // Update profile
      await profiles.update(user.id, {
        display_name: displayName.trim() || null,
        height_cm: heightCm ? parseInt(heightCm) : null,
        weight_kg: weightKg ? parseFloat(weightKg) : null,
      });

      toast.success('Profil güncellendi');
      loadUserData(); // Reload to get updated avatar URL
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Profil kaydedilemedi');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Toggle specialization
  const toggleSpecialization = (specId) => {
    setCoachSpecializations((prev) =>
      prev.includes(specId)
        ? prev.filter((s) => s !== specId)
        : [...prev, specId]
    );
  };

  // Save coach profile
  const handleSaveCoachProfile = async () => {
    if (!user) return;

    if (!coachDisplayName.trim()) {
      toast.error('Koç adı gerekli');
      return;
    }

    try {
      setIsSavingCoach(true);

      await profiles.upsertCoachProfile(user.id, {
        display_name: coachDisplayName.trim(),
        bio: coachBio.trim() || null,
        specializations: coachSpecializations,
        coach_type: coachType,
        is_accepting_clients: isAcceptingClients,
      });

      setHasCoachProfile(true);
      toast.success('Koç profili güncellendi');
    } catch (error) {
      console.error('Error saving coach profile:', error);
      toast.error('Koç profili kaydedilemedi');
    } finally {
      setIsSavingCoach(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <p className="text-gray-400">Yükleniyor...</p>
        </motion.div>
      </div>
    );
  }

  const currentAvatar = avatarPreview || avatarUrl;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Background texture */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-gray-950 to-black opacity-50" />
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-30" />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800/50">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold">Profil Ayarları</h1>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
          {/* Profile Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 backdrop-blur-sm"
          >
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Kişisel Bilgiler</h2>
                <p className="text-sm text-gray-500">Profil fotoğrafı ve görünen adınız</p>
              </div>
            </div>

            {/* Avatar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
              <div className="relative group">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-24 h-24 rounded-full bg-gray-800 border-2 border-gray-700 overflow-hidden cursor-pointer transition-all group-hover:border-orange-500/50"
                >
                  {currentAvatar ? (
                    <img
                      src={currentAvatar}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-10 h-10 text-gray-600" />
                    </div>
                  )}
                </div>

                {/* Camera overlay */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity"
                >
                  <Camera className="w-6 h-6 text-white" />
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>

              <div className="flex-1 space-y-1">
                <p className="text-sm text-gray-400">Profil Fotoğrafı</p>
                <p className="text-xs text-gray-600">JPG, PNG veya GIF. Max 5MB.</p>
                {avatarFile && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-orange-400">Yeni fotoğraf seçildi</span>
                    <button
                      onClick={() => {
                        setAvatarFile(null);
                        setAvatarPreview('');
                      }}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Display Name */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Görünen Ad
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Adınız"
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all outline-none"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  <Mail className="w-4 h-4 inline mr-1.5" />
                  E-posta
                </label>
                <div className="px-4 py-3 bg-gray-800/30 border border-gray-700/30 rounded-xl text-gray-500">
                  {user?.email}
                </div>
              </div>

              {/* Height and Weight */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <Ruler className="w-4 h-4 inline mr-1.5" />
                    Boy (cm)
                  </label>
                  <input
                    type="number"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    placeholder="175"
                    min="100"
                    max="250"
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <Scale className="w-4 h-4 inline mr-1.5" />
                    Kilo (kg)
                  </label>
                  <input
                    type="number"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    placeholder="70"
                    min="30"
                    max="300"
                    step="0.1"
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="flex items-center gap-2 px-6 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all"
              >
                {isSavingProfile ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Kaydet
              </button>
            </div>
          </motion.section>

          {/* Coach Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gray-900/50 border border-gray-800/50 rounded-2xl overflow-hidden backdrop-blur-sm"
          >
            {/* Section Header (clickable) */}
            <button
              onClick={() => setIsCoachSectionOpen(!isCoachSectionOpen)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-800/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold">Koçluk Profili</h2>
                  <p className="text-sm text-gray-500">
                    {hasCoachProfile ? 'Koç profiliniz aktif' : 'Koç olmak için profil oluşturun'}
                  </p>
                </div>
              </div>
              {isCoachSectionOpen ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Collapsible content */}
            <AnimatePresence>
              {isCoachSectionOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 space-y-6 border-t border-gray-800/50 pt-6">
                    {/* Want to be coach toggle */}
                    {!hasCoachProfile && (
                      <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-blue-400" />
                          <div>
                            <p className="font-medium">Koç Olmak İstiyorum</p>
                            <p className="text-sm text-gray-500">Öğrencilerinize koçluk yapmaya başlayın</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setWantsToBeCoach(!wantsToBeCoach)}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            wantsToBeCoach ? 'bg-blue-600' : 'bg-gray-700'
                          }`}
                        >
                          <motion.div
                            animate={{ x: wantsToBeCoach ? 24 : 2 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            className="absolute top-1 w-4 h-4 bg-white rounded-full"
                          />
                        </button>
                      </div>
                    )}

                    {/* Coach form */}
                    {(wantsToBeCoach || hasCoachProfile) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-5"
                      >
                        {/* Coach Display Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Koç Adı *
                          </label>
                          <input
                            type="text"
                            value={coachDisplayName}
                            onChange={(e) => setCoachDisplayName(e.target.value)}
                            placeholder="Öğrencilerinizin göreceği isim"
                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none"
                          />
                        </div>

                        {/* Coach Type */}
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Koçluk Türü
                          </label>
                          <select
                            value={coachType}
                            onChange={(e) => setCoachType(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none appearance-none cursor-pointer"
                          >
                            {COACH_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Bio */}
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Hakkında
                          </label>
                          <textarea
                            value={coachBio}
                            onChange={(e) => setCoachBio(e.target.value)}
                            placeholder="Deneyiminiz, uzmanlık alanlarınız, koçluk yaklaşımınız..."
                            rows={4}
                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none resize-none"
                          />
                        </div>

                        {/* Specializations */}
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-3">
                            Uzmanlık Alanları
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {SPECIALIZATIONS.map((spec) => {
                              const isSelected = coachSpecializations.includes(spec.id);
                              const Icon = spec.icon;
                              return (
                                <button
                                  key={spec.id}
                                  type="button"
                                  onClick={() => toggleSpecialization(spec.id)}
                                  className={`
                                    flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all
                                    ${isSelected
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 border border-gray-700/50'
                                    }
                                  `}
                                >
                                  <Icon className="w-4 h-4" />
                                  {spec.label}
                                  {isSelected && <Check className="w-3.5 h-3.5" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Accepting clients toggle */}
                        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <Heart className="w-5 h-5 text-green-400" />
                            <div>
                              <p className="font-medium">Yeni Öğrenci Kabul Ediyorum</p>
                              <p className="text-sm text-gray-500">Öğrenciler sizi bulabilir</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setIsAcceptingClients(!isAcceptingClients)}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              isAcceptingClients ? 'bg-green-600' : 'bg-gray-700'
                            }`}
                          >
                            <motion.div
                              animate={{ x: isAcceptingClients ? 24 : 2 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                              className="absolute top-1 w-4 h-4 bg-white rounded-full"
                            />
                          </button>
                        </div>

                        {/* Save Coach Profile Button */}
                        <div className="flex justify-end pt-2">
                          <button
                            onClick={handleSaveCoachProfile}
                            disabled={isSavingCoach}
                            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all"
                          >
                            {isSavingCoach ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            {hasCoachProfile ? 'Güncelle' : 'Koç Profili Oluştur'}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.section>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
